import React, { useState } from 'react';
import { contactInfo } from '../mock/reviewsData';
import { submitLead } from '../lib/leadSubmit';
import { Phone, Mail, MapPin, Clock, Send, MessageCircle, CheckCircle2, Facebook, Instagram, Linkedin, Twitter, Youtube, Navigation } from 'lucide-react';
import { Button } from './ui/button';

const ContactSection = () => {
  const [form, setForm] = useState({ name: '', phone: '', message: '' });
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || form.phone.length !== 10) return;
    setSending(true);
    await submitLead({ type: 'Contact Enquiry', name: form.name, phone: form.phone, message: form.message });
    setSending(false);
    setSent(true);
    setTimeout(() => { setSent(false); setForm({ name: '', phone: '', message: '' }); }, 5000);
  };

  return (
    <section id="contact" className="bg-white py-16 lg:py-24 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-1.5 rounded-full bg-emerald-50 text-emerald-700 text-sm font-medium border border-emerald-100 mb-4">Get in Touch</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0F2A4B]">Let’s talk about your cover</h2>
          <p className="mt-3 text-slate-600 text-lg max-w-2xl mx-auto">Our insurance experts are here to help you choose the right plan. Call, WhatsApp, or visit us.</p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          {/* Info cards */}
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white rounded-2xl p-6 shadow-lg">
              <div className="flex items-center gap-2 mb-4">
                <Phone className="w-5 h-5" />
                <h3 className="font-semibold">Call Our Experts</h3>
              </div>
              <div className="space-y-3">
                {contactInfo.phones.map((p) => (
                  <a key={p.number} href={`tel:${p.number.replace(/\s/g, '')}`} className="flex items-center justify-between bg-white/10 backdrop-blur rounded-xl px-4 py-3 hover:bg-white/20 transition-colors">
                    <div>
                      <div className="text-xs opacity-80">{p.label}</div>
                      <div className="font-semibold text-sm">{p.number}</div>
                    </div>
                    <Phone className="w-4 h-4" />
                  </a>
                ))}
              </div>
              <a
                href={contactInfo.socials.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 w-full flex items-center justify-center gap-2 bg-white text-emerald-700 font-semibold rounded-xl py-3 text-sm hover:bg-emerald-50 transition-colors"
              >
                <MessageCircle className="w-4 h-4" /> Chat on WhatsApp
              </a>
            </div>

            <div className="bg-white border border-slate-100 rounded-2xl p-6 hover:shadow-md transition-shadow">
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-xl bg-orange-50 text-orange-600 flex items-center justify-center shrink-0">
                  <Mail className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs text-slate-500 uppercase tracking-wider">Email Us</div>
                  <a href={`mailto:${contactInfo.email}`} className="font-semibold text-[#0F2A4B] hover:text-[#00B48E]">{contactInfo.email}</a>
                </div>
              </div>
            </div>

            <div className="bg-white border border-slate-100 rounded-2xl p-6 hover:shadow-md transition-shadow">
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                  <MapPin className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <div className="text-xs text-slate-500 uppercase tracking-wider">Visit Office</div>
                  <div className="font-semibold text-[#0F2A4B]">{contactInfo.address.line1}</div>
                  <div className="text-sm text-slate-600 leading-relaxed">
                    {contactInfo.address.line2}<br />
                    {contactInfo.address.line3}<br />
                    {contactInfo.address.city}
                  </div>
                  <a
                    href={contactInfo.mapLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 mt-2 text-xs font-semibold text-[#00B48E] hover:underline"
                  >
                    <Navigation className="w-3.5 h-3.5" /> Get Directions
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-white border border-slate-100 rounded-2xl p-6 hover:shadow-md transition-shadow">
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center shrink-0">
                  <Clock className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs text-slate-500 uppercase tracking-wider">Business Hours</div>
                  {contactInfo.hours.map((h) => (
                    <div key={h.day} className="text-sm text-slate-700">
                      <span className="font-semibold">{h.day}:</span> {h.time}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3 pt-2">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Follow us:</span>
              {[
                { Icon: Facebook, href: contactInfo.socials.facebook },
                { Icon: Instagram, href: contactInfo.socials.instagram },
                { Icon: Linkedin, href: contactInfo.socials.linkedin },
                { Icon: Twitter, href: contactInfo.socials.twitter },
                { Icon: Youtube, href: contactInfo.socials.youtube },
              ].map(({ Icon, href }, i) => (
                <a key={i} href={href} className="w-9 h-9 rounded-full bg-slate-100 hover:bg-[#00B48E] hover:text-white flex items-center justify-center text-slate-600 transition-colors">
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Map + Form */}
          <div className="lg:col-span-3 space-y-6">
            <div className="relative rounded-2xl overflow-hidden border border-slate-100 shadow-sm">
              <iframe
                title="InsureRiskFree Solutions Office"
                src={contactInfo.map}
                width="100%"
                height="360"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />

              {/* Open in Maps CTA */}
              <a
                href={contactInfo.mapLink}
                target="_blank"
                rel="noopener noreferrer"
                className="absolute bottom-4 right-4 bg-white text-[#0F2A4B] hover:text-[#00B48E] rounded-full px-4 py-2 text-xs font-semibold shadow-lg border border-slate-100 flex items-center gap-1.5 transition-colors"
              >
                Open in Google Maps <Navigation className="w-3.5 h-3.5" />
              </a>
            </div>

            <div className="bg-gradient-to-br from-slate-50 to-white rounded-2xl border border-slate-100 p-6 lg:p-8">
              <h3 className="text-xl font-bold text-[#0F2A4B] mb-1">Send us a message</h3>
              <p className="text-sm text-slate-500 mb-5">We’ll get back within 24 hours.</p>

              {sent ? (
                <div className="py-10 text-center">
                  <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="w-9 h-9 text-emerald-600" />
                  </div>
                  <h4 className="text-lg font-bold text-[#0F2A4B]">Message sent!</h4>
                  <p className="text-sm text-slate-600">Thanks {form.name || 'friend'}, we’ll reach out soon.</p>
                </div>
              ) : (
                <form onSubmit={submit} className="grid sm:grid-cols-2 gap-3">
                  <input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Your name"
                    className="px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-[#00B48E] bg-white"
                    required
                  />
                  <div className="flex items-center border border-slate-200 rounded-xl overflow-hidden focus-within:border-[#00B48E] bg-white">
                    <span className="px-3 py-3 bg-slate-50 text-slate-700 text-sm font-medium border-r">+91</span>
                    <input
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                      placeholder="Mobile number"
                      className="flex-1 px-3 py-3 text-sm outline-none"
                      required
                    />
                  </div>
                  <textarea
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="How can we help you? (optional)"
                    rows={4}
                    className="sm:col-span-2 px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-[#00B48E] bg-white resize-none"
                  />
                  <Button type="submit" disabled={sending} className="sm:col-span-2 bg-[#00B48E] hover:bg-[#00a080] text-white rounded-xl py-6 font-semibold disabled:opacity-60">
                    <Send className="w-4 h-4 mr-2" /> {sending ? 'Sending...' : 'Send Message'}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
